/**
 * Vocabulary shared by the Smart Search plan screens.
 *
 * Small, but shared on purpose: the sentinel and the tier list are the two
 * things a second copy of would silently drift on.
 */

/**
 * The API's way of saying "no ceiling".
 *
 * It never reaches the screen — every place a limit is shown converts it, and
 * the form offers a checkbox instead. A `-1` visible anywhere in this UI is a
 * bug.
 */
export const UNLIMITED = -1;

export const isUnlimited = (v) => Number(v) === UNLIMITED;

/** A limit, in words. */
export const limitText = (v, unit = "") => {
  if (isUnlimited(v)) return "Unlimited";
  if (v === null || v === undefined || v === "") return "—";
  return `${Number(v).toLocaleString("en-IN")}${unit ? ` ${unit}` : ""}`;
};

/** Rupees, printed exactly as the API reports them — nothing is rounded here. */
export const rupees = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: Math.abs(n % 1) > 0 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};

export const isActive = (plan) => plan?.is_active === 1 || plan?.is_active === true;

export const BRAND = "#5b48e8";

/** The tiers in use today. The form accepts one that is not on this list. */
export const TIERS = ["free", "solo", "pro", "firm", "enterprise"];

/**
 * A tier's chip colours.
 *
 * Ordered light to dark, because tiers genuinely are ordered — a reader can see
 * which plan sits above which without reading the word. A tier not listed here
 * gets the neutral slate, which is a fine answer rather than a missing one.
 */
export const TIER_TONE = {
  free: { color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" },
  solo: { color: "#0d9488", bg: "#f0fdfa", border: "#99f6e4" },
  pro: { color: "#4f46e5", bg: "#eef2ff", border: "#c7d2fe" },
  firm: { color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  enterprise: { color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
};

export const tierTone = (tier) =>
  TIER_TONE[String(tier || "").toLowerCase()]
  || { color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" };

/**
 * Whether a feature line is part of the plan.
 *
 * Missing means yes. The field was added after rows already existed, and the
 * backend reads an absent `included` as true — so a row written before the flag
 * must not start rendering as "not included" here.
 */
export const included = (bullet) => bullet?.included !== false;

/**
 * Feature lines in the shape the card draws them.
 *
 * Rows have been stored as plain strings and as objects, so both are accepted
 * and normalised once rather than at each place that reads them.
 */
export const featureLines = (features) => {
  const rows = Array.isArray(features?.bullets) ? features.bullets : [];
  return rows
    .map((b) => (typeof b === "string"
      ? { text: b, included: true }
      : { text: b?.text || "", included: included(b) }))
    .filter((b) => b.text);
};

/**
 * Friendly names for the price modes we know about.
 *
 * Not the list of what exists — that comes from `/plans/meta`. This only turns
 * a known code into readable words; a mode nobody here has heard of is shown as
 * the value the database holds, which is honest and still selectable.
 */
export const PRICE_MODE_LABELS = {
  paid: "Paid",
  free: "Free",
  contact: "Contact sales",
};

export const priceModeLabel = (value) =>
  PRICE_MODE_LABELS[String(value || "").toLowerCase()] || String(value || "");

/**
 * A meta list, in the one shape the selects want.
 *
 * The endpoint may answer with bare strings, `{value,label}`, `{key,label}` or
 * an object map — normalising once here means neither select has to guess, and
 * a shape change costs one function rather than two components.
 *
 * `current` is folded in so a value already on the plan is always selectable,
 * even when it is no longer offered: opening a form must never silently clear a
 * field it could not find in a list.
 */
export const toOptions = (raw, { label = (v) => v, current = "" } = {}) => {
  const rows = Array.isArray(raw)
    ? raw
    : (raw && typeof raw === "object"
      ? Object.entries(raw).map(([value, text]) => ({ value, label: text }))
      : []);

  const out = [];
  rows.forEach((r) => {
    const value = typeof r === "string" ? r : (r?.value ?? r?.key ?? r?.code ?? r?.name);
    if (!value || out.some((o) => o.value === value)) return;
    const text = typeof r === "string" ? label(r) : (r?.label || r?.title || label(value));
    out.push({ value, label: text || value });
  });

  if (current && !out.some((o) => o.value === current)) {
    out.unshift({ value: current, label: label(current) || current });
  }
  return out;
};

/** Whether this plan's card should be lifted out of the row. */
export const isHighlighted = (features) => features?.highlight === true
  || features?.highlight === 1
  || features?.highlight === "1";

/**
 * What the card should print where a price goes.
 *
 * A plan sold on enquiry has no number to show, and "₹0/mo" against it is not a
 * blank — it is a wrong price. The mode decides; the amount is printed only
 * when the mode says there is one.
 */
export const priceDisplay = (plan) => {
  const mode = String(plan?.features?.price_mode || "").toLowerCase();
  if (mode === "contact") return { text: "Contact sales", suffix: "" };
  if (mode === "free") return { text: "Free", suffix: "" };
  return { text: rupees(plan?.monthly_price_inr) ?? "—", suffix: "/mo" };
};
